// JWT Testing Utilities for Production Readiness

export const testJWTToken = async (): Promise<{
    isValid: boolean;
    expiresAt?: Date;
    error?: string;
}> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        return { isValid: false, error: 'No token found' };
    }

    try {
        // Decode JWT payload (without verification - just for testing)
        const payload = JSON.parse(atob(token.split('.')[1]));

        const now = new Date();
        const expiresAt = new Date(payload.exp * 1000);

        if (expiresAt <= now) {
            return {
                isValid: false,
                expiresAt,
                error: 'Token has expired'
            };
        }

        return {
            isValid: true,
            expiresAt,
            error: undefined
        };
    } catch (error) {
        return {
            isValid: false,
            error: 'Invalid token format'
        };
    }
};

export const testSessionTimeout = async (): Promise<{
    willExpireIn: number; // minutes
    shouldRefresh: boolean;
}> => {
    const tokenTest = await testJWTToken();

    if (!tokenTest.isValid || !tokenTest.expiresAt) {
        return { willExpireIn: 0, shouldRefresh: true };
    }

    const now = new Date();
    const willExpireIn = Math.floor((tokenTest.expiresAt.getTime() - now.getTime()) / (1000 * 60));

    // Refresh if token expires in less than 5 minutes
    const shouldRefresh = willExpireIn < 5;

    return { willExpireIn, shouldRefresh };
};

// Test API endpoint availability
export const testAPIEndpoints = async (): Promise<{
    endpoint: string;
    status: 'success' | 'error';
    responseTime: number;
    error?: string;
}[]> => {
    const endpoints = [
        '/api/health',
        '/api/parking-lots',
        '/api/analytics/dashboard',
        '/api/admin/bump-version'
    ];

    const results = await Promise.all(
        endpoints.map(async (endpoint) => {
            const startTime = Date.now();
            try {
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                });

                const responseTime = Date.now() - startTime;

                if (response.ok) {
                    return {
                        endpoint,
                        status: 'success' as const,
                        responseTime
                    };
                } else {
                    return {
                        endpoint,
                        status: 'error' as const,
                        responseTime,
                        error: `HTTP ${response.status}: ${response.statusText}`
                    };
                }
            } catch (error) {
                return {
                    endpoint,
                    status: 'error' as const,
                    responseTime: Date.now() - startTime,
                    error: error instanceof Error ? error.message : 'Unknown error'
                };
            }
        })
    );

    return results;
};

// Production readiness checklist
export const runProductionReadinessCheck = async (): Promise<{
    jwt: Awaited<ReturnType<typeof testJWTToken>>;
    sessionTimeout: Awaited<ReturnType<typeof testSessionTimeout>>;
    apiEndpoints: Awaited<ReturnType<typeof testAPIEndpoints>>;
    environment: string;
    mockDataEnabled: boolean;
}> => {
    const [jwt, sessionTimeout, apiEndpoints] = await Promise.all([
        testJWTToken(),
        testSessionTimeout(),
        testAPIEndpoints()
    ]);

    return {
        jwt,
        sessionTimeout,
        apiEndpoints,
        environment: process.env.REACT_APP_ENVIRONMENT || 'production',
        mockDataEnabled: process.env.REACT_APP_ENVIRONMENT === 'development'
    };
};
